import { Link } from 'react-router-dom';
import type { RoomTypeSummary } from '@brandonstrong/types';
import { money } from '../lib/format';

export function AvailabilityBadge({ soldOut, remaining }: { soldOut: boolean; remaining: number }) {
  if (soldOut) return <span className="label-highlight text-sm">Sold out</span>;
  if (remaining <= 3)
    return (
      <span className="bg-yellow-500 px-2.5 py-1 font-display text-xs font-black uppercase text-night-950">
        Only {remaining} left
      </span>
    );
  return (
    <span className="bg-white/10 px-2.5 py-1 font-display text-xs font-black uppercase text-ink-50">
      Available
    </span>
  );
}

/** Edem event card: photo with a blue badge, heavy uppercase title, yellow meta, pink button. */
export function RoomCard({ room, query }: { room: RoomTypeSummary; query: string }) {
  const href = `/rooms/${room.slug}${query}`;
  const [dollars, cents] = money(room.fromRate).replace('$', '').split('.');
  return (
    <article className={`flex flex-col bg-night-900 ${room.soldOut ? 'opacity-80' : ''}`}>
      <Link to={href} className="relative block aspect-[4/3] bg-night-800 bg-stage">
        {room.primaryImage ? (
          <img
            src={room.primaryImage.url}
            alt={room.primaryImage.altText ?? room.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center font-display text-4xl font-black text-white/10">
            {room.code}
          </div>
        )}
        <div className="absolute left-5 top-0">
          {room.soldOut ? (
            <span className="badge-blue !bg-pink-600 !text-white text-lg">
              Sold<span className="text-sm">out</span>
            </span>
          ) : (
            <span className="badge-blue">
              <span className="text-[11px] text-white">from</span>
              <span className="text-2xl">${dollars}</span>
              <span className="text-[11px] text-white">
                {cents === '00' ? 'per night' : `.${cents} / night`}
              </span>
            </span>
          )}
        </div>
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h2 className="text-[19px]">
            <Link to={href} className="hover:text-pink-300">
              {room.name}
            </Link>
          </h2>
          <p className="text-meta mt-1 text-sm">
            {[
              room.bedConfiguration,
              room.sizeSqft ? `${room.sizeSqft} sq ft` : null,
              room.isAccessible ? 'Accessible' : null,
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
        {room.shortDescription && <p className="text-ink-200">{room.shortDescription}</p>}
        {room.amenities.length > 0 && (
          <p className="text-sm text-ink-600">
            {room.amenities
              .slice(0, 4)
              .map((a) => a.name)
              .join(' · ')}
            {room.amenities.length > 4 ? ` · +${room.amenities.length - 4} more` : ''}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-3">
          <AvailabilityBadge soldOut={room.soldOut} remaining={room.remaining} />
          {room.soldOut ? (
            <span className="btn-outline !px-5 !py-2.5 cursor-not-allowed text-sm opacity-60">
              Sold out
            </span>
          ) : (
            <Link to={href} className="btn-accent !px-6 !py-3 text-sm">
              View room
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
